import { Button } from "@/components/ui/button";
import { X, Check, Sparkles, TrendingDown, TrendingUp } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import { Link } from "react-router-dom";

const ProblemSolution = () => {
  return (
    <section className="section-spacing bg-gradient-to-b from-background to-primary/5" id="problem-solution">
      <div className="container mx-auto px-4 lg:px-8">
        <ScrollReveal delay={100}>
          <div className="text-center mb-16 lg:mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 glass-panel rounded-full border border-primary/20">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">The Transformation</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-display font-bold mb-6 lg:mb-8 leading-tight">
              Stop Renting Content.{" "}
              <br className="hidden sm:block" />
              <span className="bg-gradient-brand bg-clip-text text-transparent">Start Owning It.</span>
            </h2>
            
            <p className="text-lg lg:text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              The difference between founders who scale and those who struggle? One builds systems they own, 
              the other rents them forever.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 max-w-7xl mx-auto">
          {/* The Problem - Agency Dependency */}
          <ScrollReveal delay={200}>
            <div className="relative">
              {/* Background glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-destructive/10 to-destructive/5 rounded-3xl blur-xl" />
              
              <div className="relative glass-panel p-6 lg:p-8 border border-destructive/20 rounded-2xl overflow-hidden">
                {/* Header badge */}
                <div className="flex items-center gap-2 mb-6">
                  <div className="p-2 bg-destructive/10 rounded-full">
                    <TrendingDown className="w-5 h-5 text-destructive" />
                  </div>
                  <span className="text-sm font-semibold text-destructive uppercase tracking-wide">
                    The Agency Trap
                  </span>
                </div>

                <h3 className="text-2xl lg:text-3xl font-display font-bold mb-6 text-foreground leading-tight">
                  The Never-Ending Payment Prison
                </h3>

                {/* Problem points */}
                <div className="space-y-4 mb-8">
                  {[
                    "₹3+ lakhs monthly bleeding—forever",
                    "Generic content that sounds like everyone else",
                    "Zero knowledge transfer to your team",
                    "Stop paying? Everything dies instantly",
                    "Own nothing, control nothing"
                  ].map((problem, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-destructive/5 hover:bg-destructive/10 transition-colors">
                      <div className="p-1 bg-destructive/20 rounded-full mt-1">
                        <X className="w-4 h-4 text-destructive" />
                      </div>
                      <span className="text-sm lg:text-base text-foreground/90 leading-relaxed font-medium">
                        {problem}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Cost breakdown */}
                <div className="p-4 lg:p-6 bg-destructive/10 rounded-xl border border-destructive/20">
                  <div className="text-center">
                    <div className="text-sm font-medium text-destructive/80 mb-2">5-Year Cost</div>
                    <div className="text-2xl lg:text-3xl font-display font-bold text-destructive mb-1">
                      ₹18+ Crores
                    </div>
                    <div className="text-sm text-foreground/70">+ Zero Ownership</div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* The Solution - Ownership Model */}
          <ScrollReveal delay={300}>
            <div className="relative">
              {/* Background glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/5 rounded-3xl blur-xl" />
              
              <div className="relative glass-panel p-6 lg:p-8 border border-primary/20 rounded-2xl overflow-hidden">
                {/* Floating particles effect */}
                <div className="absolute top-4 right-4 w-2 h-2 bg-primary rounded-full animate-pulse" />
                <div className="absolute top-8 right-8 w-1 h-1 bg-accent rounded-full animate-pulse opacity-60" style={{animationDelay: '0.5s'}} />
                
                {/* Header badge */}
                <div className="flex items-center gap-2 mb-6">
                  <div className="p-2 bg-primary/10 rounded-full">
                    <TrendingUp className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-sm font-semibold text-primary uppercase tracking-wide">
                    The Ownership Revolution
                  </span>
                </div>

                <h3 className="text-2xl lg:text-3xl font-display font-bold mb-6 text-foreground leading-tight">
                  Build. Own. Scale. Forever.
                </h3>

                {/* Solution points */}
                <div className="space-y-4 mb-8">
                  {[
                    "One-time ₹5-12L investment, lifetime ownership",
                    "Elite team trained exclusively for YOUR brand",
                    "Battle-tested systems + SOPs become yours",
                    "90-day independence guarantee",
                    "YOU own people, systems, IP, everything"
                  ].map((solution, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 hover:bg-primary/10 transition-colors">
                      <div className="p-1 bg-primary/20 rounded-full mt-1">
                        <Check className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-sm lg:text-base text-foreground/90 leading-relaxed font-medium">
                        {solution}
                      </span>
                    </div>
                  ))}
                </div>

                {/* ROI breakdown */}
                <div className="p-4 lg:p-6 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border border-primary/20 mb-6">
                  <div className="text-center">
                    <div className="text-sm font-medium text-primary/80 mb-2">5-Year Value</div>
                    <div className="text-2xl lg:text-3xl font-display font-bold bg-gradient-brand bg-clip-text text-transparent mb-1">
                      ₹15+ Crores Saved
                    </div>
                    <div className="text-sm text-foreground/70">+ Complete Ownership</div>
                  </div>
                </div>

                {/* CTA Button */}
                <Button 
                  variant="hero" 
                  size="lg" 
                  className="w-full text-sm lg:text-base min-h-[48px] lg:min-h-[56px] font-semibold"
                  onClick={() => {
                    const element = document.querySelector('#calculator');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                >
                  Calculate My Transformation
                </Button>
              </div>
            </div>
          </ScrollReveal>
        </div>

        {/* Bottom CTA Section */}
        <ScrollReveal delay={400}>
          <div className="mt-16 lg:mt-20 text-center">
            <div className="max-w-4xl mx-auto p-6 lg:p-8 glass-panel rounded-2xl border border-accent/20">
              <h4 className="text-xl lg:text-2xl font-display font-bold mb-4 text-foreground">
                Ready to Stop Renting and Start Owning?
              </h4>
              <p className="text-base lg:text-lg text-muted-foreground mb-6 leading-relaxed">
                Join 47+ companies that chose ownership over dependency and never looked back.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/process">
                  <Button variant="hero" size="lg" className="min-h-[48px] font-semibold">
                    See The Process
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="min-h-[48px] font-semibold"
                  onClick={() => {
                    const element = document.querySelector('#calculator');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                >
                  Calculate ROI
                </Button>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default ProblemSolution;